import type { GameState, CardDef, GameAction } from '../types.js';
import { getInstance, getPlayerState } from '../engine/GameState.js';
import {
  canPlayCharacter,
  canSupport,
  canPlayAction,
  canPlayReaction,
  canAssignBlocker,
} from '../engine/Rules.js';
import { previewTargets, getMaxHandSize } from '../engine/AbilityProcessor.js';
import { setDragSuppressed, hideTooltip, showTooltipForCard } from './CardView.js';
import { showCardDetail } from './CardDetailView.js';

const TAP_THRESHOLD_PX = 8;

/** Hold-duration threshold for tap-to-open-detail. A release that lands at
 *  or above this many milliseconds after pointerdown is treated as a
 *  long-press peek (the existing tooltip-preview gesture) and does NOT
 *  open the detail modal. Quick taps under the threshold open the modal. */
const LONG_PRESS_MS = 500;

/** Half-angle of the upward wedge in which a hand-card drag can initiate.
 *  50° → 100° total wedge, lenient enough that dragging the leftmost
 *  hand card to the rightmost ring slot (or vice versa) doesn't escape
 *  the wedge. Tighten if accidental drags during slide-to-preview become
 *  noticeable. */
const HAND_DRAG_WEDGE_HALF_ANGLE_DEG = 50;
const HAND_DRAG_WEDGE_TAN = Math.tan((HAND_DRAG_WEDGE_HALF_ANGLE_DEG * Math.PI) / 180);

type SourceKind = 'hand-character' | 'hand-action' | 'hand-reaction' | 'ring-character';

type DropKind =
  | 'ring-slot'                 // play character into empty slot
  | 'ring-character-support'    // support a ring character
  | 'ring-character-join'       // join united attack as a joiner
  | 'battle-line'               // play action / stage attacker
  | 'attacker-zone'             // (silent) any spot in opponent's half stages an attacker
  | 'unstage-zone'              // (silent) drag back to your own half to cancel staging
  | 'opponent-attacker'         // assign blocker to opponent's attacking group
  | 'unblock-zone'              // (silent) drag a blocker back to your side to cancel its block
  | 'trash';                    // discard when over hand limit

interface EligibleDropTarget {
  el: HTMLElement;
  kind: DropKind;
  slotIndex?: number;
  targetInstanceId?: string;
  groupIndex?: number;
  silent?: boolean;  // hit-test only, no visible highlight — used for big
                     // forgiving zones that share behavior with a named target
                     // (e.g. opponent-half drops stage an attacker like battle-line).
}

interface DragSession {
  pointerId: number;
  sourceEl: HTMLElement;
  sourceKind: SourceKind;
  instanceId: string;
  startClientX: number;
  startClientY: number;
  startRectLeft: number;
  startRectTop: number;
  startRectWidth: number;
  startRectHeight: number;
  /** Timestamp of pointerdown — used by handleTap to distinguish a quick
   *  tap (opens detail modal) from a long-press peek (tooltip-only). */
  startTime: number;
  dropTargets: EligibleDropTarget[];
  currentTarget: EligibleDropTarget | null;
  hasMovedPastThreshold: boolean;
  // Invisible flex item inserted where the source card sat so that siblings
  // don't shift when we pull the card out of flow. Only used for hand drags.
  placeholderEl: HTMLElement | null;
  // Last hand card we've shown the tooltip for during a slide-to-preview
  // (pre-drag pointer drag across hand cards). Used to skip redundant
  // tooltip rebuilds when the pointer is still over the same card.
  lastHoverInstanceId: string | null;
  // True while the snap-back animation is in flight. A new pointerdown
  // arriving in this window should force-complete the snap-back rather
  // than be ignored, so tapping a different card right after a missed
  // drop kicks off a fresh drag immediately.
  snappingBack: boolean;
}

export interface DragDeps {
  board: HTMLElement;
  getState: () => GameState | null;
  getCatalog: () => Map<string, CardDef>;
  getStagedAttackers: () => Map<string, string[]>; // primaryId → [primary, ...joiners]
  getStagedBlockers: () => Map<number, string>;    // groupIndex → blockerInstanceId
  stageAttacker: (primaryId: string) => void;
  unstageAttacker: (primaryId: string) => void;
  joinAttacker: (primaryId: string, joinerId: string) => void;
  unjoinAttacker: (joinerId: string) => void;
  unstageBlocker: (blockerInstanceId: string) => void;
  onAction: (action: GameAction) => void;
  refresh: () => void;
}

export class DragController {
  private deps: DragDeps;
  private session: DragSession | null = null;

  constructor(deps: DragDeps) {
    this.deps = deps;
    this.deps.board.addEventListener('pointerdown', this.onPointerDown);
  }

  destroy(): void {
    this.deps.board.removeEventListener('pointerdown', this.onPointerDown);
    this.endSession();
  }

  private onPointerDown = (e: PointerEvent): void => {
    // If the previous session is still snapping back to its origin, a new
    // tap should kick off a fresh session immediately. Force-complete the
    // snap-back's cleanup (which would normally fire after the 260ms
    // transition) so we can clear `this.session` and proceed below.
    if (this.session && this.session.snappingBack) {
      this.completeSnapBackCleanup(this.session);
      this.session = null;
    }
    if (this.session) return;
    if (e.button !== undefined && e.button !== 0) return;

    const state = this.deps.getState();
    if (!state || state.winner) return;

    const target = e.target as HTMLElement;
    if (target.closest('.activate-btn, button, .action-button')) return;

    const cardEl = target.closest('.card[data-instance-id]') as HTMLElement | null;
    if (!cardEl) return;
    // Face-down cards have no public identity to show — skip the tap path
    // entirely so we don't spoil hidden cards.
    if (cardEl.classList.contains('face-down')) return;

    const instanceId = cardEl.dataset.instanceId!;
    const sourceKind = this.classify(cardEl, instanceId, state);
    if (!sourceKind) {
      // Face-up card we don't own (opponent's ring/hand). No drag possible,
      // but a quick tap should open the detail modal.
      this.beginViewOnlyTap(e, cardEl);
      return;
    }

    const dropTargets = this.computeDropTargets(sourceKind, instanceId, state);
    const isHand = sourceKind === 'hand-character'
                || sourceKind === 'hand-action'
                || sourceKind === 'hand-reaction';
    // Ring characters get to drag even without drop targets so tap-to-cancel
    // works on staged attackers/blockers that have no other legal moves.
    // Hand cards ALWAYS get a session — even non-playable ones — so the
    // user can slide their finger to a different, possibly playable, hand
    // card and play that one. The actual drag-vs-no-drag decision is
    // gated on `s.dropTargets.length > 0` once the wedge is crossed.
    if (dropTargets.length === 0 && !isHand && sourceKind !== 'ring-character') return;
    if (dropTargets.length === 0 && !isHand && !this.isStaged(instanceId)) {
      // Ring character with no drag affordance — fall back to the view-only
      // tap path so tap-to-open-detail still works (e.g. exhausted ring
      // characters during the player's own turn).
      this.beginViewOnlyTap(e, cardEl);
      return;
    }

    const rect = cardEl.getBoundingClientRect();
    this.session = {
      pointerId: e.pointerId,
      sourceEl: cardEl,
      sourceKind,
      instanceId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startRectLeft: rect.left,
      startRectTop: rect.top,
      startRectWidth: rect.width,
      startRectHeight: rect.height,
      startTime: performance.now(),
      dropTargets,
      currentTarget: null,
      hasMovedPastThreshold: false,
      placeholderEl: null,
      lastHoverInstanceId: null,
      snappingBack: false,
    };

    // Visual focus indicator — mirrors the :hover lift but is JS-driven
    // so it follows the slide-to-preview focus instead of staying pinned
    // on whichever card the pointer is technically over.
    cardEl.classList.add('preview-focused');

    // While a drag session is active, suppress the browser's natural
    // :hover lift on cards. Pointer capture sticks :hover on the original
    // tapped card, so without this, the originally-tapped card stays
    // visually lifted alongside whichever card we've moved focus to.
    this.deps.board.classList.add('drag-session-active');

    try { cardEl.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    // Listen at window-level so we still receive pointerup/move/cancel even
    // if the browser releases pointer capture mid-drag (Chrome does this
    // intermittently — once that happens, events route to the hit-test
    // target, not cardEl, and a listener attached to cardEl never fires).
    // pointermove/up/cancel still bubble to window regardless of capture
    // state, so this is the reliable attachment point.
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);

    e.preventDefault();
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.session || e.pointerId !== this.session.pointerId) return;
    const s = this.session;

    if (!s.hasMovedPastThreshold) {
      const dx = e.clientX - s.startClientX;
      const dy = e.clientY - s.startClientY;
      if (Math.hypot(dx, dy) < TAP_THRESHOLD_PX) return;

      // Hand cards in PORTRAIT only initiate a drag when the pointer moves
      // UP within the configured wedge (see HAND_DRAG_WEDGE_HALF_ANGLE_DEG).
      // The wedge + slide-to-preview makes touch interaction with a wide
      // hand row predictable, but on landscape (desktop, tablet-landscape,
      // phone-landscape) the screen is wider than tall and the wedge
      // becomes unhelpful — players want to drag in any direction toward
      // the ring. So in landscape we skip the wedge entirely; the threshold
      // alone gates drag-initiation.
      const isHand = s.sourceKind === 'hand-character'
                  || s.sourceKind === 'hand-action'
                  || s.sourceKind === 'hand-reaction';
      const isPortrait = window.innerHeight > window.innerWidth;
      if (isHand && isPortrait) {
        const inUpwardWedge = dy < 0 && Math.abs(dx) <= Math.abs(dy) * HAND_DRAG_WEDGE_TAN;
        if (!inUpwardWedge) {
          this.previewCardUnderPointer(e.clientX, e.clientY, s);
          return;
        }
      }
      // Whether we're in portrait or landscape, a non-playable hand card
      // (no drop targets) shouldn't lift — that gesture is a no-op until
      // the player slides back onto a playable card or releases.
      if (isHand && s.dropTargets.length === 0) return;

      // Anchor the drag to the pointer's CURRENT position so the card
      // lifts in place — wherever the pointer is on the card (corner,
      // edge, middle) becomes the grip point for the rest of the drag.
      // We only adjust startClientX/Y; startRectLeft/Top stay at the
      // card's natural in-flow position so the subsequent transform
      // leaves the card right where it sits, and snap-back can lerp
      // back to the actual hand cell. Without this, slide-to-preview
      // would have left startClientX pinned at the new card's entry
      // edge, making the card jump sideways at drag-start to put the
      // pointer back at that edge.
      if (isHand) {
        s.startClientX = e.clientX;
        s.startClientY = e.clientY;
      }

      s.hasMovedPastThreshold = true;
      this.beginVisualDrag();
    }

    // Recompute dx/dy after the threshold block — startClientX/Y may have
    // been reset above when the drag begins (recentering the card on the
    // pointer). Initially after a reset both are 0, placing the card
    // exactly where we positioned it; subsequent moves grow normally.
    const dx = e.clientX - s.startClientX;
    const dy = e.clientY - s.startClientY;
    s.sourceEl.style.transform = `translate(${s.startRectLeft + dx}px, ${s.startRectTop + dy}px)`;
    this.updateCurrentTarget(e.clientX, e.clientY);
  };

  /** Pre-drag slide-to-preview: while the pointer is held but no drag has
   *  begun, retarget the session to whichever player-hand card the pointer
   *  is currently over. The tooltip switches to the new card, the drag
   *  origin (start position, source element, drop targets) resets to it,
   *  and a subsequent upward gesture initiates a drag on the *new* card —
   *  not the originally-tapped one.
   *
   *  Pointer capture redirects pointer events to the source element, so
   *  the neighbor's own pointerenter never fires — we hit-test manually
   *  with elementFromPoint. */
  private previewCardUnderPointer(clientX: number, clientY: number, s: DragSession): void {
    const hit = document.elementFromPoint(clientX, clientY);
    const cardEl = hit
      ? ((hit as Element).closest('.card[data-instance-id]') as HTMLElement | null)
      : null;
    const inPlayerHand = cardEl?.closest('.hand-area[data-side="player"]');

    if (!cardEl || !inPlayerHand) {
      // Slid off any player-hand card (onto the ring, battle line, empty
      // space, etc.). Drop the tooltip but keep the session alive so a
      // slide-back picks the focus back up.
      if (s.lastHoverInstanceId !== null) {
        hideTooltip();
        s.lastHoverInstanceId = null;
      }
      return;
    }

    const instanceId = cardEl.dataset.instanceId;
    if (!instanceId || instanceId === s.lastHoverInstanceId) return;

    const state = this.deps.getState();
    const catalog = this.deps.getCatalog();
    if (!state || !catalog) return;
    let inst;
    try { inst = getInstance(state, instanceId); } catch { return; }
    const def = catalog.get(inst.cardId);
    if (!def) return;

    const sourceKind = this.classify(cardEl, instanceId, state);
    if (!sourceKind) return;
    const dropTargets = this.computeDropTargets(sourceKind, instanceId, state);
    const rect = cardEl.getBoundingClientRect();

    // Retarget the session to this card. Drag origin resets to the
    // current pointer position so the next pointermove's wedge check
    // measures from "here" rather than from the originally-tapped card.
    s.sourceEl.classList.remove('preview-focused');
    s.sourceEl = cardEl;
    s.sourceKind = sourceKind;
    s.instanceId = instanceId;
    s.startClientX = clientX;
    s.startClientY = clientY;
    s.startRectLeft = rect.left;
    s.startRectTop = rect.top;
    s.startRectWidth = rect.width;
    s.startRectHeight = rect.height;
    s.dropTargets = dropTargets;
    s.lastHoverInstanceId = instanceId;
    cardEl.classList.add('preview-focused');

    hideTooltip();
    showTooltipForCard(cardEl, clientX, clientY, def, inst);
  }

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.session || e.pointerId !== this.session.pointerId) return;
    const s = this.session;

    try { s.sourceEl.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);

    if (!s.hasMovedPastThreshold) {
      this.handleTap();
      this.endSession();
      return;
    }

    // Drag completed. If over a valid target we dispatch and end immediately;
    // otherwise we animate the snap-back before tearing down.
    //
    // Dispatch is deferred by one microtask so this `pointerup` listener
    // fully unwinds before downstream render logic destroys s.sourceEl. In
    // Chrome, removing the captured pointer's source element while pointer
    // cleanup is still in flight can wedge the page (intermittent hang on
    // hand-card play / blocker assignment).
    if (s.currentTarget) {
      const captured = s;
      queueMicrotask(() => {
        if (this.session !== captured) return;
        this.finishDrop();
        this.endSession();
      });
    } else {
      this.snapBack();
    }
  };

  private onPointerCancel = (e: PointerEvent): void => {
    if (!this.session || e.pointerId !== this.session.pointerId) return;
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    this.endSession();
  };

  private beginVisualDrag(): void {
    if (!this.session) return;
    const s = this.session;

    // The `dragging` class will visualize the lift now, so drop the
    // pre-drag preview-focused indicator from the source card.
    s.sourceEl.classList.remove('preview-focused');

    setDragSuppressed(true);
    // For hand drags, insert an invisible placeholder where the source card
    // sat so siblings don't reflow. Ring-slot sources don't need this — the
    // slot itself is a fixed-size container.
    if (
      s.sourceKind === 'hand-character' ||
      s.sourceKind === 'hand-action' ||
      s.sourceKind === 'hand-reaction'
    ) {
      const placeholder = document.createElement('div');
      placeholder.className = 'drag-placeholder';
      placeholder.style.width = `${s.startRectWidth}px`;
      placeholder.style.height = `${s.startRectHeight}px`;
      s.sourceEl.parentNode?.insertBefore(placeholder, s.sourceEl);
      s.placeholderEl = placeholder;
    }
    // Pin size before applying position: fixed so the card doesn't collapse
    // when removed from flex flow.
    s.sourceEl.style.width = `${s.startRectWidth}px`;
    s.sourceEl.style.height = `${s.startRectHeight}px`;
    s.sourceEl.classList.add('dragging');

    for (const t of s.dropTargets) {
      if (t.silent) continue;
      const cls = this.highlightClassFor(t.kind);
      if (cls) t.el.classList.add(cls);
    }

    if (s.sourceKind === 'hand-action' || s.sourceKind === 'hand-reaction') {
      this.applyPreviewHighlights();
    }
  }

  // Maps a drop kind to the CSS class that visually marks it as a valid
  // target. Silent targets (attacker-zone, unstage-zone) also return null.
  private highlightClassFor(kind: DropKind): string | null {
    switch (kind) {
      case 'ring-slot': return 'drag-valid-target';
      case 'ring-character-support': return 'support-target';
      case 'ring-character-join': return 'drag-valid-target';
      case 'battle-line': return 'drag-valid-target';
      case 'opponent-attacker': return 'drag-valid-target';
      case 'trash': return 'drag-valid-target';
      case 'attacker-zone':
      case 'unstage-zone':
      case 'unblock-zone':
        return null;
    }
  }

  private applyPreviewHighlights(): void {
    if (!this.session) return;
    const state = this.deps.getState();
    if (!state) return;
    const preview = previewTargets(state, this.session.instanceId, this.deps.getCatalog());

    for (const targetId of preview.targets) {
      const el = this.deps.board.querySelector(
        `.ring-slot .card[data-instance-id="${targetId}"]`,
      ) as HTMLElement | null;
      if (el) el.classList.add('preview-target');
    }
    if (preview.hitsOpponentHp) {
      const hp = this.deps.board.querySelector('.player-info[data-side="opponent"] .hp-bar-container');
      if (hp) hp.classList.add('preview-target');
    }
    if (preview.hitsSelfHp) {
      const hp = this.deps.board.querySelector('.player-info[data-side="player"] .hp-bar-container');
      if (hp) hp.classList.add('preview-target');
    }
    if (preview.hitsSelfDeck) {
      const deck = this.deps.board.querySelector('.player-info[data-side="player"] .deck-count');
      if (deck) deck.classList.add('preview-target');
    }
  }

  private updateCurrentTarget(x: number, y: number): void {
    if (!this.session) return;
    const s = this.session;
    const under = document.elementFromPoint(x, y) as HTMLElement | null;
    const next = this.findTargetContaining(under);
    if (next !== s.currentTarget) {
      if (s.currentTarget && !s.currentTarget.silent) s.currentTarget.el.classList.remove('drag-over');
      if (next && !next.silent) next.el.classList.add('drag-over');
      s.currentTarget = next;
    }
    // When hovering over a silent attacker-zone, pulse the battle-line so the
    // player still gets visual feedback that staging/playing is valid.
    if (
      s.sourceKind === 'ring-character' ||
      s.sourceKind === 'hand-action' ||
      s.sourceKind === 'hand-reaction'
    ) {
      const battleLine = this.deps.board.querySelector('.battle-line') as HTMLElement | null;
      if (battleLine) {
        const overAttackerZone = !!next && (next.kind === 'attacker-zone' || next.kind === 'battle-line');
        battleLine.classList.toggle('drag-over', overAttackerZone);
      }
    }
  }

  private findTargetContaining(el: HTMLElement | null): EligibleDropTarget | null {
    if (!el || !this.session) return null;
    for (const t of this.session.dropTargets) {
      if (t.el === el || t.el.contains(el)) return t;
    }
    return null;
  }

  private handleTap(): void {
    if (!this.session) return;
    const s = this.session;

    // Try the existing unstage / unjoin / unblock actions first. A tap on a
    // staged piece is meant to undo that staging, not pop the info modal.
    if (s.sourceKind === 'ring-character') {
      const staged = this.deps.getStagedAttackers();
      if (staged.has(s.instanceId)) {
        this.deps.unstageAttacker(s.instanceId);
        this.deps.refresh();
        return;
      }
      for (const [primaryId, members] of staged) {
        if (members.includes(s.instanceId) && s.instanceId !== primaryId) {
          this.deps.unjoinAttacker(s.instanceId);
          this.deps.refresh();
          return;
        }
      }
      for (const [, blockerId] of this.deps.getStagedBlockers()) {
        if (blockerId === s.instanceId) {
          this.deps.unstageBlocker(s.instanceId);
          this.deps.refresh();
          return;
        }
      }
    }

    // No game-state action ran — fall through to the info modal. Skip when
    // the press was held long enough to count as a tooltip-preview gesture
    // (so a player peeking at their hand doesn't get an unwanted modal on
    // release).
    if (performance.now() - s.startTime >= LONG_PRESS_MS) return;
    const cardId = s.sourceEl.dataset.cardId;
    if (!cardId) return;
    hideTooltip();
    showCardDetail(cardId, this.deps.getCatalog());
  }

  /** Tap-only path for cards that don't get a drag session (opponent
   *  face-up cards, or player ring characters with no drag affordance).
   *  Tracks movement + duration locally without touching `this.session`,
   *  so it can run alongside the regular drag flow without coupling. */
  private beginViewOnlyTap(e: PointerEvent, cardEl: HTMLElement): void {
    // Only honor taps on cards that live in a real board zone — filters out
    // cards rendered in tooltips, log links, modals, etc.
    if (!cardEl.closest('.hand-area, .ring-slot')) return;
    const cardId = cardEl.dataset.cardId;
    if (!cardId) return;

    const startTime = performance.now();
    const startX = e.clientX;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    let moved = false;

    const cleanup = (): void => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
    const onMove = (me: PointerEvent): void => {
      if (me.pointerId !== pointerId) return;
      if (!moved && Math.hypot(me.clientX - startX, me.clientY - startY) >= TAP_THRESHOLD_PX) {
        moved = true;
      }
    };
    const onUp = (ue: PointerEvent): void => {
      if (ue.pointerId !== pointerId) return;
      cleanup();
      if (moved) return;
      if (performance.now() - startTime >= LONG_PRESS_MS) return;
      hideTooltip();
      showCardDetail(cardId, this.deps.getCatalog());
    };
    const onCancel = (ce: PointerEvent): void => {
      if (ce.pointerId !== pointerId) return;
      cleanup();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    e.preventDefault();
  }

  private finishDrop(): void {
    if (!this.session) return;
    const s = this.session;
    const target = s.currentTarget;
    if (!target) return;

    switch (target.kind) {
      case 'ring-slot':
        this.deps.onAction({
          type: 'PLAY_CHARACTER',
          instanceId: s.instanceId,
          slotIndex: target.slotIndex!,
        });
        break;
      case 'ring-character-support':
        this.deps.onAction({
          type: 'SUPPORT_CHARACTER',
          handInstanceId: s.instanceId,
          ringInstanceId: target.targetInstanceId!,
        });
        break;
      case 'battle-line':
      case 'attacker-zone':
        if (s.sourceKind === 'ring-character') {
          this.deps.stageAttacker(s.instanceId);
          this.deps.refresh();
        } else if (s.sourceKind === 'hand-action') {
          this.deps.onAction({ type: 'PLAY_ACTION', instanceId: s.instanceId });
        } else if (s.sourceKind === 'hand-reaction') {
          // PLAY_REACTION is the counter-phase entry point (defender response
          // to an attack). On the owner's own main phase the same card just
          // resolves through PLAY_ACTION like any other action.
          const phase = this.deps.getState()?.phase;
          this.deps.onAction({
            type: phase === 'counter' ? 'PLAY_REACTION' : 'PLAY_ACTION',
            instanceId: s.instanceId,
          });
        }
        break;
      case 'unstage-zone': {
        const staged = this.deps.getStagedAttackers();
        if (staged.has(s.instanceId)) {
          this.deps.unstageAttacker(s.instanceId);
        } else if (this.isJoiner(s.instanceId)) {
          this.deps.unjoinAttacker(s.instanceId);
        }
        this.deps.refresh();
        break;
      }
      case 'ring-character-join':
        this.deps.joinAttacker(target.targetInstanceId!, s.instanceId);
        this.deps.refresh();
        break;
      case 'opponent-attacker':
        this.deps.onAction({
          type: 'ASSIGN_BLOCKER',
          groupIndex: target.groupIndex!,
          blockerInstanceId: s.instanceId,
        });
        break;
      case 'trash':
        this.deps.onAction({ type: 'DISCARD_CARD', instanceId: s.instanceId });
        break;
      case 'unblock-zone':
        this.deps.onAction({ type: 'UNASSIGN_BLOCKER', blockerInstanceId: s.instanceId });
        break;
    }
  }

  // Animate the dragged card back to its origin (placeholder slot) using a
  // CSS transform transition, THEN tear down. Keeping position: fixed during
  // the animation means `transform` stays anchored to the viewport, so the
  // card actually lerps from the release point to the home rect.
  private snapBack(): void {
    if (!this.session) return;
    const s = this.session;
    s.snappingBack = true;

    // Strip highlights / preview immediately; only the card itself animates.
    for (const t of s.dropTargets) {
      t.el.classList.remove('drag-valid-target', 'drag-over', 'support-target');
    }
    const battleLine = this.deps.board.querySelector('.battle-line');
    if (battleLine) battleLine.classList.remove('drag-over');
    this.deps.board
      .querySelectorAll('.preview-target')
      .forEach(el => el.classList.remove('preview-target'));

    const el = s.sourceEl;
    el.classList.add('snap-back');
    // Target: the card's original viewport position (matches the placeholder).
    el.style.transform = `translate(${s.startRectLeft}px, ${s.startRectTop}px)`;

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      el.removeEventListener('transitionend', finish);
      // Only clean up if this snap-back is still the active session. A new
      // tap during the snap-back would have already force-completed cleanup
      // and cleared this.session.
      if (this.session === s) {
        this.completeSnapBackCleanup(s);
        this.session = null;
      }
    };
    el.addEventListener('transitionend', finish);
    // Safety: transitionend doesn't fire if the element is removed mid-
    // transition or if no visual change occurred.
    setTimeout(finish, 260);
  }

  /** Restore the source card to its natural in-flow state after a
   *  snap-back. Shared between the regular transitionend / timeout path
   *  and the force-cleanup path triggered when a new tap arrives during
   *  the snap-back window. */
  private completeSnapBackCleanup(s: DragSession): void {
    const el = s.sourceEl;
    // Suppress transitions during the frame-teardown switch, otherwise the
    // base `.card { transition: all 0.2s ease }` re-animates the transform
    // change from `translate(X,Y)` → `none` once position flips from
    // fixed to static — producing a phantom second lerp.
    el.style.transition = 'none';
    el.classList.remove('dragging', 'snap-back', 'preview-focused');
    el.style.width = '';
    el.style.height = '';
    el.style.transform = '';
    // Force a reflow so the above changes are committed with transition:
    // none before we restore the stylesheet-driven transition.
    void el.offsetWidth;
    el.style.transition = '';

    setDragSuppressed(false);
    if (s.placeholderEl?.parentNode) s.placeholderEl.remove();
    this.deps.board.classList.remove('drag-session-active');
  }

  private endSession(): void {
    if (!this.session) return;
    const s = this.session;

    for (const t of s.dropTargets) {
      t.el.classList.remove('drag-valid-target', 'drag-over', 'support-target');
    }
    // Battle-line may have been toggled by updateCurrentTarget independent of
    // whether it was in dropTargets (when hovering silent attacker zones).
    const battleLine = this.deps.board.querySelector('.battle-line');
    if (battleLine) battleLine.classList.remove('drag-over');
    this.deps.board
      .querySelectorAll('.preview-target')
      .forEach(el => el.classList.remove('preview-target'));

    if (s.hasMovedPastThreshold) {
      s.sourceEl.classList.remove('dragging');
      s.sourceEl.style.width = '';
      s.sourceEl.style.height = '';
      s.sourceEl.style.transform = '';
      setDragSuppressed(false);
    }
    // Always clear the slide-preview focus indicator — even on tap-only
    // sessions that never crossed the drag threshold.
    s.sourceEl.classList.remove('preview-focused');
    // Restore the natural :hover lift now that the session has ended.
    this.deps.board.classList.remove('drag-session-active');

    // Remove the hand placeholder if one was inserted. On a valid drop that
    // dispatches an action, the subsequent re-render would wipe the hand
    // area anyway, but explicitly removing is simpler than branching.
    if (s.placeholderEl && s.placeholderEl.parentNode) {
      s.placeholderEl.remove();
    }

    this.session = null;
  }

  private classify(cardEl: HTMLElement, instanceId: string, state: GameState): SourceKind | null {
    const catalog = this.deps.getCatalog();
    const inst = getInstance(state, instanceId);
    const def = catalog.get(inst.cardId);
    if (!def) return null;

    const parent = cardEl.closest('.hand-area, .ring-slot') as HTMLElement | null;
    if (!parent) return null;

    if (parent.classList.contains('hand-area')) {
      if (parent.dataset.side !== 'player') return null;
      if (def.type === 'character') return 'hand-character';
      if (def.type === 'action') {
        return def.subtype === 'reaction' ? 'hand-reaction' : 'hand-action';
      }
      return null;
    }

    if (parent.classList.contains('ring-slot')) {
      if (parent.dataset.side !== 'player') return null;
      if (def.type !== 'character') return null;
      return 'ring-character';
    }

    return null;
  }

  private computeDropTargets(
    sourceKind: SourceKind,
    instanceId: string,
    state: GameState,
  ): EligibleDropTarget[] {
    const board = this.deps.board;
    const catalog = this.deps.getCatalog();
    const targets: EligibleDropTarget[] = [];

    if (sourceKind === 'hand-character') {
      board.querySelectorAll('.ring-area[data-side="player"] .ring-slot').forEach((slot) => {
        const slotEl = slot as HTMLElement;
        const slotIndex = parseInt(slotEl.dataset.slotIndex!, 10);
        if (canPlayCharacter(state, instanceId, slotIndex, catalog)) {
          targets.push({ el: slotEl, kind: 'ring-slot', slotIndex });
        }
      });
      board
        .querySelectorAll('.ring-area[data-side="player"] .card[data-instance-id]')
        .forEach((card) => {
          const el = card as HTMLElement;
          const ringId = el.dataset.instanceId!;
          if (canSupport(state, instanceId, ringId, catalog)) {
            targets.push({ el, kind: 'ring-character-support', targetInstanceId: ringId });
          }
        });
    }

    // Hand actions & reactions: battle-line is the visual target, and the
    // opponent half is a silent catch-all so the drop zone isn't fiddly
    // (matches how attacker staging works for ring characters).
    // Reactions are playable both during the defender's counter phase
     // (resolves as PLAY_REACTION) and during the owner's main phase as a
     // normal action (resolves as PLAY_ACTION).
    if (
      ((sourceKind === 'hand-action' || sourceKind === 'hand-reaction') &&
        canPlayAction(state, instanceId, catalog)) ||
      (sourceKind === 'hand-reaction' && canPlayReaction(state, instanceId, catalog))
    ) {
      const battleLine = board.querySelector('.battle-line') as HTMLElement | null;
      if (battleLine) targets.push({ el: battleLine, kind: 'battle-line' });

      const oppZones = [
        '.ring-area[data-side="opponent"]',
        '.hand-area[data-side="opponent"]',
        '.player-info[data-side="opponent"]',
      ];
      for (const sel of oppZones) {
        const el = board.querySelector(sel) as HTMLElement | null;
        if (el) targets.push({ el, kind: 'attacker-zone', silent: true });
      }
    }

    if (sourceKind === 'ring-character') {
      if (state.phase === 'main' && state.currentPlayer === 'player') {
        const inst = getInstance(state, instanceId);
        if (!inst.isFresh && !inst.isExhausted) {
          const staged = this.deps.getStagedAttackers();
          const isPrimary = staged.has(instanceId);
          const isJoiner = this.isJoiner(instanceId);
          const alreadyInSomeGroup = isPrimary || isJoiner;

          if (!alreadyInSomeGroup) {
            // Battle-line is the visual indicator; the whole opponent half is
            // also a valid drop (silent) so the drop zone isn't fiddly.
            const battleLine = board.querySelector('.battle-line') as HTMLElement | null;
            if (battleLine) targets.push({ el: battleLine, kind: 'battle-line' });

            const oppZones = [
              '.ring-area[data-side="opponent"]',
              '.hand-area[data-side="opponent"]',
              '.player-info[data-side="opponent"]',
            ];
            for (const sel of oppZones) {
              const el = board.querySelector(sel) as HTMLElement | null;
              if (el) targets.push({ el, kind: 'attacker-zone', silent: true });
            }

            // Join targets: existing staged primaries' cards.
            for (const [primaryId, members] of staged) {
              if (primaryId === instanceId) continue;
              if (members.includes(instanceId)) continue;
              if (members.length >= 3) continue;
              const primaryEl = board.querySelector(
                `.ring-area[data-side="player"] .card[data-instance-id="${primaryId}"]`,
              ) as HTMLElement | null;
              if (primaryEl) {
                targets.push({
                  el: primaryEl,
                  kind: 'ring-character-join',
                  targetInstanceId: primaryId,
                });
              }
            }
          } else {
            // Already staged — dragging back to the player's own half unstages.
            const unstageZones = [
              '.ring-area[data-side="player"]',
              '.hand-area[data-side="player"]',
              '.player-info[data-side="player"]',
            ];
            for (const sel of unstageZones) {
              const el = board.querySelector(sel) as HTMLElement | null;
              if (el) targets.push({ el, kind: 'unstage-zone', silent: true });
            }
          }
        }
      } else if (state.phase === 'counter' && state.currentPlayer !== 'player') {
        const inst = getInstance(state, instanceId);
        if (!inst.isExhausted && state.combatState) {
          // Is this character already assigned as a blocker?
          let currentAssignedGroup: number | null = null;
          for (const [gIdx, bId] of state.combatState.blockerAssignments) {
            if (bId === instanceId) { currentAssignedGroup = gIdx; break; }
          }

          // Eligible attacker groups to drop onto. For unassigned blockers,
          // respect canAssignBlocker. For already-assigned ones, allow every
          // other group — the engine's ASSIGN_BLOCKER handler swaps the
          // assignment atomically, and canAssignBlocker would forbid it.
          for (let gIdx = 0; gIdx < state.combatState.groups.length; gIdx++) {
            if (gIdx === currentAssignedGroup) continue;
            const isEligible = currentAssignedGroup !== null
              ? true
              : canAssignBlocker(state, gIdx, instanceId, catalog);
            if (!isEligible) continue;
            const group = state.combatState.groups[gIdx];
            // Surface every attacker in the group (primary + any joiners)
            // as a drop target. All resolve to the same group index — for
            // a united attack the player can drop the blocker on any of
            // the cards involved and it reads as "block this group",
            // which matches what the eye expects.
            for (const attackerId of group.attackerIds) {
              const el = board.querySelector(
                `.ring-area[data-side="opponent"] .card[data-instance-id="${attackerId}"]`,
              ) as HTMLElement | null;
              if (el) targets.push({ el, kind: 'opponent-attacker', groupIndex: gIdx });
            }
          }

          // Drag-back to cancel the block. Player's own half is the silent
          // unstage zone. Handled at drop via a new 'unblock-zone' kind.
          if (currentAssignedGroup !== null) {
            const backZones = [
              '.ring-area[data-side="player"]',
              '.hand-area[data-side="player"]',
              '.player-info[data-side="player"]',
            ];
            for (const sel of backZones) {
              const el = board.querySelector(sel) as HTMLElement | null;
              if (el) targets.push({ el, kind: 'unblock-zone', silent: true });
            }
          }
        }
      }
    }

    // Trash drop target — any hand card can be discarded while the player
    // is over their hand limit, regardless of phase. This lets players who
    // drew into overhand dig themselves out before pressing End Turn.
    if (
      (sourceKind === 'hand-character' ||
        sourceKind === 'hand-action' ||
        sourceKind === 'hand-reaction') &&
      state.currentPlayer === 'player'
    ) {
      const playerPs = getPlayerState(state, 'player');
      const maxHand = getMaxHandSize(state, 'player', catalog);
      if (playerPs.hand.length > maxHand) {
        const trash = board.querySelector('.hand-trash') as HTMLElement | null;
        if (trash) targets.push({ el: trash, kind: 'trash' });
      }
    }

    return targets;
  }

  private isJoiner(instanceId: string): boolean {
    const staged = this.deps.getStagedAttackers();
    for (const [primaryId, members] of staged) {
      if (members.includes(instanceId) && primaryId !== instanceId) return true;
    }
    return false;
  }

  private isStaged(instanceId: string): boolean {
    const staged = this.deps.getStagedAttackers();
    if (staged.has(instanceId)) return true;
    if (this.isJoiner(instanceId)) return true;
    for (const [, blockerId] of this.deps.getStagedBlockers()) {
      if (blockerId === instanceId) return true;
    }
    return false;
  }
}
